import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { get, post } from '../api';
import { ALLOWED_DOMAIN } from '../config';
import { clearTimelineCache } from '../utils/timelineCache';
// Sliding-window auto-logout. The 30-min countdown resets every time
// the user does ANYTHING in any TIB tab (main app OR defo popups) —
// click, key, scroll, mouse-move, touch. It does NOT pause while the
// tab is in the background: if the user opens TIB then alt-tabs to
// Gmail for 30 min, the timer runs out and they get logged out on
// return. "Inactivity" here means literal lack of input in the app,
// nothing else.
//
// Cross-tab sync uses a BroadcastChannel so activity in one TIB tab
// resets the timer in every other TIB tab. Broadcasts are throttled
// to ~5s so a stream of mousemove events doesn't flood the channel.
const INACTIVITY_TIMEOUT = 30 * 60 * 1000;       // 30 minutes
const ACTIVITY_BROADCAST_CHANNEL  = 'tib-activity';
const ACTIVITY_BROADCAST_THROTTLE_MS = 5000;
const PROFILE_POLL_INTERVAL = 15 * 1000;   // 15 seconds (normal users)
const PENDING_POLL_INTERVAL = 3 * 1000;    // 3 seconds while waiting for role assignment

const AuthContext = createContext(null);

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  // eslint-disable-next-line no-unused-vars
  const [profileLoading, setProfileLoading] = useState(false);
  const timerRef = useRef(null);
  const pollRef = useRef(null);
  const pollFailCountRef = useRef(0);

  const logout = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    // Clear the httpOnly cookie server-side
    post('/auth/logout').catch(() => {});
    // Drop the cached dashboard timeline so the next session loads fresh.
    clearTimelineCache();
    setUser(null);
    setUserProfile(null);
  }, []);

  // Reset inactivity timer
  const resetTimer = useCallback(() => {
    if (!user) return;
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = setTimeout(() => {
      logout();
    }, INACTIVITY_TIMEOUT);
  }, [user, logout]);

  // Google sign-in handler — called by Login component
  // NOTE: we intentionally do NOT set profileLoading here. Setting it
  // causes AuthContext.loading→true which makes AppRoutes swap Login for
  // LoadingScreen, unmounting Login. If the API call then fails the
  // error is thrown back to a now-unmounted component and silently lost.
  // React 18 batches setUser+setUserProfile into one render, so
  // WaitingRoom never flashes for users who already have roles.
  const signInWithGoogle = useCallback(async (credentialResponse) => {
    try {
      const googleToken = credentialResponse.credential;

      // Decode JWT to check domain client-side
      let email = '';
      try {
        const payload = JSON.parse(atob(googleToken.split('.')[1]));
        email = payload.email || '';
      } catch {
        throw new Error('Invalid sign-in token received. Please try again.');
      }
      if (!email.endsWith(`@${ALLOWED_DOMAIN}`)) {
        throw new Error(`Access restricted to @${ALLOWED_DOMAIN} accounts only.`);
      }

      // Exchange Google token — server sets httpOnly cookie automatically
      const data = await post('/auth/google', { token: googleToken });

      setUser({
        email: data.user.email,
        displayName: data.user.display_name,
        photoURL: data.user.photo_url,
      });
      setUserProfile({
        id: data.user.id,
        email: data.user.email,
        displayName: data.user.display_name,
        photoURL: data.user.photo_url,
        roles: data.user.roles || [],
        createdAt: data.user.created_at,
      });
    } catch (err) {
      console.error('Auth error:', err);
      setUser(null);
      setUserProfile(null);
      throw err;
    }
  }, []);

  // On mount: check if we have a valid cookie by fetching profile
  useEffect(() => {
    get('/users/me')
      .then((profile) => {
        setUser({
          email: profile.email,
          displayName: profile.display_name,
          photoURL: profile.photo_url,
        });
        setUserProfile({
          id: profile.id,
          email: profile.email,
          displayName: profile.display_name,
          photoURL: profile.photo_url,
          roles: profile.roles || [],
          createdAt: profile.created_at,
        });
      })
      .catch(() => {
        setUser(null);
        setUserProfile(null);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  // Poll profile (replaces Firestore real-time listener). Cadence:
  //   - 3s while the user is pending (no roles yet) so WaitingRoom flips to
  //     the app within seconds of the admin assigning a role.
  //   - 15s once approved.
  const pendingMode = !!user && (!userProfile || (userProfile.roles || []).length === 0);
  useEffect(() => {
    if (!user) return;

    const interval = pendingMode ? PENDING_POLL_INTERVAL : PROFILE_POLL_INTERVAL;
    pollFailCountRef.current = 0;
    pollRef.current = setInterval(() => {
      get('/users/me')
        .then((profile) => {
          pollFailCountRef.current = 0;
          const wasPending = pendingMode;
          const nowApproved = (profile.roles || []).length > 0;
          setUserProfile({
            id: profile.id,
            email: profile.email,
            displayName: profile.display_name,
            photoURL: profile.photo_url,
            roles: profile.roles || [],
            createdAt: profile.created_at,
          });
          // Pending → approved transition: hard reload so the app boots
          // cleanly into the portal selector with fresh state.
          if (wasPending && nowApproved) {
            window.location.reload();
          }
        })
        .catch(() => {
          pollFailCountRef.current += 1;
          if (pollFailCountRef.current >= 5) {
            clearInterval(pollRef.current);
            pollRef.current = null;
          }
        });
    }, interval);

    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [user, pendingMode]);

  // Listen for 401 events dispatched by api.js
  useEffect(() => {
    const handleExpiry = () => logout();
    window.addEventListener('auth:expired', handleExpiry);
    return () => window.removeEventListener('auth:expired', handleExpiry);
  }, [logout]);

  // Inactivity timer — only runs once the user has been approved (has roles).
  // Pending users stay signed in indefinitely so they don't have to keep
  // re-authenticating while waiting for an admin.
  //
  // Behaviour summary:
  //   1. The 30-min countdown is armed the moment we mount; any input
  //      event in ANY TIB tab resets it.
  //   2. There's NO visibility pause — if the user alt-tabs to Gmail
  //      or minimises the browser, the timer keeps ticking. After 30
  //      idle minutes (regardless of where the user is) → logout.
  //   3. Cross-tab broadcasts (BroadcastChannel) propagate activity
  //      from defo popup tabs to the main inspection tab and back, so
  //      heads-down work in a defo tab still keeps the main tab's
  //      session alive (and vice versa). Broadcasts are throttled to
  //      ~5s so mousemove doesn't flood the channel.
  useEffect(() => {
    if (!user) return;
    if (pendingMode) return;

    // BroadcastChannel may be unavailable in very old browsers; degrade
    // gracefully (each tab keeps its own timer, no cross-tab sync).
    let channel = null;
    try {
      if (typeof BroadcastChannel !== 'undefined') {
        channel = new BroadcastChannel(ACTIVITY_BROADCAST_CHANNEL);
      }
    } catch {
      channel = null;
    }

    let lastBroadcastAt = 0;

    function broadcastActivity() {
      if (!channel) return;
      const now = Date.now();
      if (now - lastBroadcastAt < ACTIVITY_BROADCAST_THROTTLE_MS) return;
      lastBroadcastAt = now;
      try { channel.postMessage({ type: 'activity' }); } catch {}
    }

    function handleLocalActivity() {
      resetTimer();
      broadcastActivity();
    }

    if (channel) {
      channel.onmessage = (e) => {
        if (e?.data?.type !== 'activity') return;
        resetTimer();
      };
    }

    const activityEvents = ['mousedown', 'keydown', 'scroll', 'touchstart', 'mousemove'];
    activityEvents.forEach((event) => {
      window.addEventListener(event, handleLocalActivity, { passive: true });
    });

    // Arm the timer unconditionally. The user gets 30 min from mount
    // (or last activity) regardless of tab visibility.
    resetTimer();

    return () => {
      activityEvents.forEach((event) => {
        window.removeEventListener(event, handleLocalActivity);
      });
      if (channel) {
        channel.onmessage = null;
        try { channel.close(); } catch {}
      }
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [user, pendingMode, resetTimer]);

  const value = {
    user,
    userProfile,
    loading: loading || profileLoading,
    logout,
    signInWithGoogle,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
